/**
 * interfaces
 */
import { IUnitChildModel } from './interfaces';
import { IUnitChildDto } from '../../interfaces';

/**
 * base model
 */
import { UnitBaseModel } from '../unit-base-model';

/**
 * View model for a lazy-loaded child unit (GET units/{id}/get_units).
 *
 * Extends {@link UnitBaseModel} — all common getters and {@link hasChildrenBadge}
 * are inherited. This class only adds the fields specific to {@link IUnitChildDto}:
 * flat ID references for type, icon, and facility (no nested type object).
 *
 * Each model declares its own named DTO (`unitChildDto`) for clear,
 * type-safe access to its shape-specific fields.
 *
 * @example
 * ```ts
 * const model = new UnitChildModel(dto);
 * console.log(model.name);                  // "Room 02"   (base getter)
 * console.log(model.unitChildDto.type_id);  // "type-uuid" (own DTO)
 * console.log(model.typeId);                // "type-uuid" (own getter)
 * ```
 */
export class UnitChildModel extends UnitBaseModel<IUnitChildDto> implements IUnitChildModel<IUnitChildDto> {

    /**
     * @param unitChildDto - Raw child unit DTO from the lazy-load endpoint.
     */
    constructor(dto: IUnitChildDto) {
        super(dto);
    }

    // ─── IUnitChildDto-specific getters ──────────────────────────────────────

    /**
     * Unit type ID reference.
     */
    public get typeId(): string {
        return this.dto.type_id;
    }

    /**
     * Icon ID reference, or null.
     */
    public get iconId(): string | null {
        return this.dto.icon_id;
    }

    /**
     * Parent facility ID reference, or null.
     */
    public get facilityId(): string | null {
        return this.dto.facility_id;
    }

}
